/**
 * Customer Identity, Address & Storefront Modal Reliability — Phase 4.
 *
 * Extracted from `components/retail/customer/ShipToAddressPanel.jsx`
 * (unchanged logic, moved so it can be shared) so the pre-product-
 * selection address step in WelcomeModal and the at-checkout step in
 * ShipToAddressPanel run the EXACT SAME validate-address request shape
 * and the EXACT SAME "does the carrier's suggestion actually differ"
 * comparison — one definition, not two independently-maintained copies.
 */

// Field-name bridge between the storefront's address shape and the
// public validate-address endpoint, which uses the same names, so this
// is mostly a pass-through — kept as an explicit function so the one
// place the two shapes could diverge is obvious.
export function toValidateAddressBody(addr) {
  return {
    name: addr.name || "",
    street1: addr.street1 || "",
    street2: addr.street2 || "",
    city: addr.city || "",
    region: addr.region || "",
    postal: addr.postal || "",
    country: addr.country || "US",
  };
}

// A suggestion only matters to the customer if it actually differs from
// what they typed — comparing every physical-address field, ignoring
// case/whitespace so trivial normalisation (e.g. carrier upper-casing
// the state) doesn't trigger a confirmation for nothing.
export function suggestionDiffersFromSubmitted(submitted, suggestion) {
  if (!suggestion) return false;
  const norm = (v) => String(v || "").trim().toLowerCase();
  return ["street1", "street2", "city", "region", "postal", "country"].some(
    (k) => norm(submitted[k]) !== norm(suggestion[k])
  );
}

// WelcomeModal's structured delivery/shipping address uses
// line1/line2/state/postal_code (matches customers.addresses[] and the
// delivery_address payload shape); the validate-address endpoint and
// ShipToAddressForm use street1/street2/region/postal. Bridges between
// the two so WelcomeModal can call validation with its own field names
// without re-deriving this mapping.
export function welcomeAddressToValidateBody(addr, name) {
  return toValidateAddressBody({
    name: name || addr.name || "",
    street1: addr.line1 || "",
    street2: addr.line2 || "",
    city: addr.city || "",
    region: addr.state || "",
    postal: addr.postal_code || "",
    country: addr.country || "US",
  });
}

// Inverse of `welcomeAddressToValidateBody` — converts a validate-address
// response's `suggestion` (street1/region/postal shape) back into
// WelcomeModal's line1/state/postal_code shape, so accepting a carrier
// suggestion can merge straight into `deliveryAddress` state without the
// two field-name conventions ever leaking into each other.
export function suggestionToWelcomeShape(suggestion) {
  if (!suggestion) return {};
  return {
    line1: suggestion.street1 || "",
    line2: suggestion.street2 || "",
    city: suggestion.city || "",
    state: suggestion.region || "",
    postal_code: suggestion.postal || "",
    country: suggestion.country || "US",
  };
}

// Cheap, order-insensitive fingerprint of the physical-address fields
// only (never name/instructions) — used to detect "the customer edited
// the address after it was validated" so a stale validation can never
// be reused across an edit.
export function addressFingerprint(addr) {
  const norm = (v) => String(v || "").trim().toLowerCase();
  return ["line1", "line2", "city", "state", "postal_code", "country"]
    .map((k) => norm(addr?.[k]))
    .join("|");
}
